import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { ArrowRight, CheckCircle2, RefreshCw, Send, AlertCircle, Check } from 'lucide-react';
import {
  getDetailedOrderInvoiceRevisionPreview,
  useApplyOrderInvoiceRevision,
} from '../../features/invoicing/api';
import type { CreateOrderInvoiceResponse, InvoiceDraft } from '../../features/invoicing/types';
import type { QboAccountingCatalog } from '../../features/invoicing/accountingApi';
import { SearchableQuickBooksServiceSelect } from '../InvoiceDialog/SearchableQuickBooksServiceSelect';
import '../InvoiceDialog/searchable-qbo-service.scss';
import '../InvoiceDialog/invoice-accounting.scss';
import {
  diffBmpInvoiceLines,
  diffOrderOwnedHeaders,
  isBmpManagedLine,
} from './invoiceRevisionDiff';
import './InvoiceRevisionPanel.scss';

type Props = {
  invoiceDateOverride?: string | null;
  dueDateOverride?: string | null;
  draft: InvoiceDraft;
  accountingCatalog?: QboAccountingCatalog | null;
  onInvoiceUpdated?: (orderId: string, result: CreateOrderInvoiceResponse) => void;
  onReload: () => Promise<unknown>;
};
const money = (cents: number | null | undefined) =>
  cents == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
const empty = (text: string | null | undefined) => text?.trim() || '—';
type RevisionAddress = {
  name?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};
function formatRevisionAddress(address?: RevisionAddress | null): string {
  if (!address) return '';
  return [
    address.name,
    address.line1,
    address.line2,
    [address.city, address.state, address.postalCode].filter(Boolean).join(' '),
    address.country,
  ]
    .filter(Boolean)
    .join(', ');
}
const HEADER_LABELS: Record<string, string> = {
  billTo: 'Bill To',
  shipTo: 'Ship To',
  shipFrom: 'Ship From',
  txnDate: 'Invoice date',
  dueDate: 'Due date',
  poNumber: 'PO number',
  ticketNumber: 'DeFeo Ticket #',
  shipDate: 'Shipping date',
  shipVia: 'Ship via',
  trackingNumber: 'Tracking number',
};

export function InvoiceRevisionPanel({
  draft,
  onInvoiceUpdated,
  onReload,
  invoiceDateOverride,
  dueDateOverride,
  accountingCatalog,
}: Props) {
  const invoiceId = draft.existingInvoice?.quickBooksInvoiceId;
  const query = useQuery({
    queryKey: ['org:invoice-revision', draft.orderId, invoiceId],
    queryFn: () => getDetailedOrderInvoiceRevisionPreview(draft.orderId),
    enabled: Boolean(invoiceId),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
  const mutation = useApplyOrderInvoiceRevision();
  const [classSelection, setClassSelection] = useState('');
  const [locationSelection, setLocationSelection] = useState('');
  const current = query.data?.existingQbo;
  // Empty means 'keep QBO current'. Existing classification is never removed by this form.
  const classChanged = Boolean(classSelection && classSelection !== current?.header.classRef?.id);
  const locationChanged = Boolean(
    locationSelection && locationSelection !== current?.header.locationRef?.id
  );
  const postedLinesVerified = Boolean(draft.postedQuickBooksLines?.length);
  const sentBefore = Boolean(
    draft.existingInvoice?.sentAt ||
    current?.emailStatus === 'EmailSent' ||
    draft.existingInvoice?.status === 'SENT'
  );

  const changes = useMemo(() => {
    const oldLines = draft.postedQuickBooksLines ?? [];
    const newLines = query.data?.newManagedLines ?? [];
    const itemChanges = diffBmpInvoiceLines(oldLines, newLines);
    const qboHeader = current?.header;
    const bySource = (source: string) => {
      const mapping = query.data?.mappings.find((m) => m.source === source);
      if (!mapping) return null;

      // QBO can return enhanced custom-field IDs (100000000x) even when the
      // saved mapping uses its original short catalog ID (1, 2, ...).
      const fields = qboHeader?.customFields ?? [];
      const normalize = (name: string) => name.trim().toLowerCase();
      const expectedName = normalize(mapping.quickBooksFieldName || '');
      if (!expectedName) return null;

      const byId = fields.filter(
        (field) =>
          field.definitionId === mapping.quickBooksDefinitionId &&
          normalize(field.name || '') === expectedName
      );
      if (byId.length === 1) return byId[0].value;

      const byName = fields.filter((field) => normalize(field.name || '') === expectedName);
      return byName.length === 1 ? byName[0].value : null;
    };
    const headerChanges = diffOrderOwnedHeaders(
      {
        billTo: formatRevisionAddress(qboHeader?.billTo),
        shipTo: formatRevisionAddress(qboHeader?.shipTo),
        shipFrom: formatRevisionAddress(qboHeader?.shipFrom),
        txnDate: qboHeader?.txnDate,
        dueDate: qboHeader?.dueDate,
        poNumber: bySource('PO_NUMBER'),
        ticketNumber: bySource('DEFEO_TICKET_NUMBER'),
        shipDate: qboHeader?.shipDate,
        shipVia: qboHeader?.shipVia,
        trackingNumber: qboHeader?.trackingNumber,
      },
      Object.fromEntries(
        Object.entries({
          ...(query.data?.proposedHeader ?? {}),
          ...(invoiceDateOverride ? { txnDate: invoiceDateOverride } : {}),
          ...(dueDateOverride ? { dueDate: dueDateOverride } : {}),
        }).map(([k, v]) => [
          k,
          ['billTo', 'shipTo', 'shipFrom'].includes(k)
            ? formatRevisionAddress(v as RevisionAddress | null)
            : v,
        ])
      ) as Record<string, string | null | undefined>,
      HEADER_LABELS
    );
    const manualAmount = oldLines
      .filter((line) => !isBmpManagedLine(line.description))
      .reduce((sum, line) => sum + line.amountCents, 0);
    const revisedManagedAmount = query.data?.revisedBmpManagedTotalCents ?? null;
    return {
      itemChanges,
      headerChanges,
      estimatedTotal: revisedManagedAmount == null ? null : revisedManagedAmount + manualAmount,
      hasChanges:
        itemChanges.length > 0 || headerChanges.length > 0 || classChanged || locationChanged,
    };
  }, [
    draft.postedQuickBooksLines,
    query.data,
    current,
    invoiceDateOverride,
    dueDateOverride,
    classChanged,
    locationChanged,
  ]);

  async function submit(send: boolean) {
    if (!invoiceId || !current?.canRevise || !changes.hasChanges) return;
    try {
      const result = await mutation.mutateAsync({
        orderId: draft.orderId,
        send,
        header: {
          ...(invoiceDateOverride ? { txnDate: invoiceDateOverride } : {}),
          ...(dueDateOverride ? { dueDate: dueDateOverride } : {}),
        },
        ...(classChanged ? { classId: classSelection } : {}),
        ...(locationChanged ? { locationId: locationSelection } : {}),
      });
      onInvoiceUpdated?.(draft.orderId, result);
      setClassSelection('');
      setLocationSelection('');
      toast.success(
        send
          ? 'Existing QuickBooks invoice updated and sent.'
          : 'Existing QuickBooks invoice updated. No email was sent.'
      );
      await Promise.all([query.refetch(), onReload()]);
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(e.response?.data?.message ?? e.message ?? 'Invoice revision failed.');
      await Promise.allSettled([query.refetch(), onReload()]);
    }
  }

  if (query.isPending || (query.isFetching && !query.data)) {
    return (
      <div className="invoice-revision__loading">
        <RefreshCw size={16} className="invoice-revision__spin" /> Reading current QuickBooks and
        saved BMP order…
      </div>
    );
  }
  if (query.isError)
    return (
      <div className="invoice-revision__alert">
        <AlertCircle size={17} />
        Could not load the QuickBooks comparison.
        <button type="button" onClick={() => query.refetch()}>
          Retry
        </button>
      </div>
    );
  if (!query.data || !current) return null;

  return (
    <section className="invoice-revision" aria-label="Review changes to existing invoice">
      <div className="invoice-revision__heading">
        <div>
          <span className="invoice-revision__eyebrow">
            QUICKBOOKS INVOICE #{draft.existingInvoice?.quickBooksDocNumber ?? invoiceId}
          </span>
          <h3>Review order changes</h3>
          <p>
            BMP-owned order values remain read-only here. You can choose a QuickBooks Class or
            Location below.
          </p>
        </div>
        <span className="invoice-revision__status">
          <CheckCircle2 size={15} /> Same invoice ID
        </span>
      </div>
      {(accountingCatalog?.classEnabled || accountingCatalog?.locationEnabled) && (
        <div className="invoice-dialog__grid invoice-dialog__grid--4">
          {accountingCatalog?.classEnabled && (
            <div className="invoice-field invoice-accounting-field">
              <label htmlFor={`qbo-revision-class-${draft.orderId}`}>QuickBooks Class</label>
              <small>Current: {current.header.classRef?.name ?? 'Unassigned'}</small>
              <SearchableQuickBooksServiceSelect
                id={`qbo-revision-class-${draft.orderId}`}
                ariaLabel="QuickBooks Class"
                emptyLabel="Keep current"
                searchPlaceholder="Search QuickBooks classes…"
                noMatchesLabel="No matching QuickBooks classes"
                value={classSelection}
                onChange={setClassSelection}
                disabled={!current.canRevise || mutation.isPending}
                options={accountingCatalog.classes.map((option) => ({
                  value: option.id,
                  label: option.name,
                }))}
              />
            </div>
          )}
          {accountingCatalog?.locationEnabled && (
            <div className="invoice-field invoice-accounting-field">
              <label htmlFor={`qbo-revision-location-${draft.orderId}`}>QuickBooks Location</label>
              <small>Current: {current.header.locationRef?.name ?? 'Unassigned'}</small>
              <SearchableQuickBooksServiceSelect
                id={`qbo-revision-location-${draft.orderId}`}
                ariaLabel="QuickBooks Location"
                emptyLabel="Keep current"
                searchPlaceholder="Search QuickBooks locations…"
                noMatchesLabel="No matching QuickBooks locations"
                value={locationSelection}
                onChange={setLocationSelection}
                disabled={!current.canRevise || mutation.isPending}
                options={accountingCatalog.locations.map((option) => ({
                  value: option.id,
                  label: option.name,
                }))}
              />
            </div>
          )}
        </div>
      )}
      {!postedLinesVerified && (
        <div className="invoice-revision__alert">
          <AlertCircle size={16} />
          QuickBooks line details could not be verified. Reopen the invoice before attempting a
          revision.
        </div>
      )}
      {!current.canRevise && (
        <div className="invoice-revision__alert">
          <AlertCircle size={16} />
          This invoice has a payment or linked transaction. Review it directly in QuickBooks.
        </div>
      )}
      {!changes.hasChanges ? (
        <div className="invoice-revision__nothing">
          <Check size={18} />
          <span>Invoice already matches the saved BMP order. No changes to apply.</span>
        </div>
      ) : (
        <>
          {changes.headerChanges.length > 0 && (
            <div className="invoice-revision__change-section">
              <div className="invoice-revision__section-title">
                <h4>Changed invoice fields</h4>
                <p>
                  Only values sourced from the saved BMP order are shown. Editing here is disabled.
                </p>
              </div>
              <div className="invoice-revision__field-head">
                <span>Field</span>
                <span>Current QuickBooks</span>
                <span>From BMP order</span>
              </div>
              <div className="invoice-revision__fields">
                {changes.headerChanges.map((change) => (
                  <div className="invoice-revision__field" key={change.key}>
                    <strong className="invoice-revision__field-label">{change.label}</strong>
                    <span className="invoice-revision__previous">{empty(change.before)}</span>
                    <span className="invoice-revision__proposed">{empty(change.after)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {changes.itemChanges.length > 0 && (
            <div className="invoice-revision__change-section">
              <div className="invoice-revision__section-title">
                <h4>Changed product / service lines</h4>
                <p>
                  Unchanged lines, including manual QuickBooks services, are hidden but preserved.
                </p>
              </div>
              <div className="invoice-revision__line-changes">
                {changes.itemChanges.map((change) => (
                  <div className="invoice-revision__line-change" key={change.key}>
                    <div className="invoice-revision__item-title">{change.title}</div>
                    <div className="invoice-revision__line-pair">
                      <div>
                        <small>Current QuickBooks</small>
                        {change.previous ? (
                          <>
                            <strong>{money(change.previous.amountCents)}</strong>
                            <span>
                              {change.previous.quantity ?? '—'} ×{' '}
                              {change.previous.unitPrice == null
                                ? '—'
                                : money(Math.round(change.previous.unitPrice * 100))}
                            </span>
                            <span>{change.previous.description}</span>
                          </>
                        ) : (
                          <strong>Not on invoice</strong>
                        )}
                      </div>
                      <ArrowRight size={18} className="invoice-revision__arrow" />
                      <div>
                        <small>From BMP order</small>
                        {change.proposed ? (
                          <>
                            <strong>{money(change.proposed.amountCents)}</strong>
                            <span>
                              {change.proposed.quantity ?? '—'} ×{' '}
                              {change.proposed.unitPrice == null
                                ? '—'
                                : money(Math.round(change.proposed.unitPrice * 100))}
                            </span>
                            <span>{change.proposed.description}</span>
                          </>
                        ) : (
                          <strong>Removed</strong>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="invoice-revision__totals">
                <div>
                  <small>Current QBO total</small>
                  <strong>{money(current.totalAmountCents)}</strong>
                </div>
                <ArrowRight size={18} />
                <div>
                  <small>Estimated revised total</small>
                  <strong>{money(changes.estimatedTotal)}</strong>
                </div>
              </div>
              <p className="invoice-revision__hint">
                Manual QuickBooks services are preserved. QuickBooks recalculates tax and the final
                total when updated.
              </p>
            </div>
          )}
        </>
      )}
      <div className="invoice-revision__actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => submit(false)}
          disabled={
            !current.canRevise ||
            !postedLinesVerified ||
            !changes.hasChanges ||
            mutation.isPending ||
            query.isFetching
          }
        >
          <RefreshCw size={16} /> {mutation.isPending ? 'Updating…' : 'Update invoice only'}
        </button>
        {sentBefore && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => submit(true)}
            disabled={
              !current.canRevise ||
              !postedLinesVerified ||
              !changes.hasChanges ||
              mutation.isPending ||
              query.isFetching
            }
          >
            <Send size={16} /> {mutation.isPending ? 'Updating & sending…' : 'Update & resend'}
          </button>
        )}
        {!sentBefore && (
          <span className="invoice-revision__hint">
            Use “Send existing invoice” when ready to email it.
          </span>
        )}
      </div>
    </section>
  );
}
